import React, { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, MessageCircle, AlertCircle } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import SEO, { orgJsonLd, breadcrumbJsonLd } from "../components/SEO";
import { FIRM_PHONE_DISPLAY, FIRM_PHONE_E164, FIRM_EMAIL, FIRM_ADDRESS_AR, FIRM_ADDRESS_EN, getPageWaLink } from "../data/firm";

export default function Contact() {
  const { lang } = useI18n();
  const isAr = lang === "ar";
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", { ...form, locale: lang });
      toast.success(isAr ? "شكراً لك. سيتواصل معك أحد الشركاء قريباً." : "Thank you. A partner will be in touch shortly.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(isAr ? "حدث خطأ. يرجى المحاولة مرة أخرى." : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO
        path="/contact"
        titleAr="تواصل معنا | مجموعة الدخيل القانونية الكويت"
        titleEn="Contact Us | Aldakheel Legal Group Kuwait"
        descriptionAr="تواصل مع مجموعة الدخيل القانونية: هاتف +965 6965 6963، بريد contact@aldakheel.legal، أو احجز استشارة عبر الواتساب."
        descriptionEn="Contact Aldakheel Legal Group: phone +965 6965 6963, email contact@aldakheel.legal, or book via WhatsApp."
        jsonLd={[orgJsonLd(), breadcrumbJsonLd([
          { name: isAr ? "الرئيسية" : "Home", path: "/" },
          { name: isAr ? "تواصل" : "Contact", path: "/contact" },
        ])]}
      />

      <section className="section-y" style={{ background: "var(--paper)" }}>
        <div className="container-luxe text-center max-w-3xl">
          <p className="eyebrow">{isAr ? "تواصل" : "Contact"}</p>
          <h1 className={`mt-3 ${isAr ? "font-arabic" : "font-display"} gold-rule center`}
              style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)", fontWeight: isAr ? 800 : 500, color: "var(--heading)", lineHeight: 1.2 }}>
            {isAr ? "تواصل مع مجموعة الدخيل" : "Reach the firm"}
          </h1>
          <p className={`mt-8 ${isAr ? "font-arabic" : ""} text-lg`} style={{ color: "var(--ink-soft)" }}>
            {isAr ? "نرد على كل استفسار خلال يوم عمل واحد." : "We respond to inbound enquiries within one business day."}
          </p>
        </div>
      </section>

      <section className="section-y" style={{ background: "var(--bg)" }}>
        <div className="container-luxe grid lg:grid-cols-12 gap-12">
          {/* CONTACT INFO */}
          <div className="lg:col-span-5 space-y-6">
            <div className="luxe-card p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-sm flex items-center justify-center"
                     style={{ background: "var(--beige)", color: "var(--gold-deep)" }}>
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <p className="eyebrow">{isAr ? "هاتف" : "Phone"}</p>
                  <a href={`tel:${FIRM_PHONE_E164}`} dir="ltr"
                     className={`mt-1 block ${isAr ? "font-arabic" : ""} text-lg font-medium`}
                     style={{ color: "var(--heading)" }}>{FIRM_PHONE_DISPLAY}</a>
                </div>
              </div>
            </div>

            <a href={getPageWaLink("contact", lang)} target="_blank" rel="noopener noreferrer"
               data-testid="contact-whatsapp-card"
               className="luxe-card p-6 block hover:bg-emerald-50">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-sm flex items-center justify-center"
                     style={{ background: "rgba(8,55,40,0.10)", color: "var(--wa)" }}>
                  <MessageCircle className="w-5 h-5" />
                </div>
                <div>
                  <p className="eyebrow">{isAr ? "واتساب" : "WhatsApp"}</p>
                  <p className={`mt-1 ${isAr ? "font-arabic" : ""} text-sm`} style={{ color: "var(--ink-soft)" }}>
                    {isAr ? "اضغط لفتح محادثة فورية" : "Tap to open an instant chat"}
                  </p>
                </div>
              </div>
            </a>

            <div className="luxe-card p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-sm flex items-center justify-center"
                     style={{ background: "var(--beige)", color: "var(--gold-deep)" }}>
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <p className="eyebrow">{isAr ? "البريد" : "Email"}</p>
                  <a href={`mailto:${FIRM_EMAIL}`} dir="ltr"
                     className={`mt-1 block ${isAr ? "font-arabic" : ""} text-base font-medium`}
                     style={{ color: "var(--heading)" }}>{FIRM_EMAIL}</a>
                </div>
              </div>
            </div>

            <div className="luxe-card p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-sm flex items-center justify-center shrink-0"
                     style={{ background: "var(--beige)", color: "var(--gold-deep)" }}>
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <p className="eyebrow">{isAr ? "المكتب" : "Office"}</p>
                  <p className={`mt-1 ${isAr ? "font-arabic" : ""} text-base font-medium`} style={{ color: "var(--heading)" }}>
                    {isAr ? FIRM_ADDRESS_AR : FIRM_ADDRESS_EN}
                  </p>
                </div>
              </div>
            </div>

            <div className="luxe-card p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-sm flex items-center justify-center shrink-0"
                     style={{ background: "var(--beige)", color: "var(--gold-deep)" }}>
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <p className="eyebrow">{isAr ? "ساعات العمل" : "Hours"}</p>
                  <p className={`mt-1 ${isAr ? "font-arabic" : ""} text-sm leading-relaxed`} style={{ color: "var(--ink)" }}>
                    {isAr ? "الأحد - الخميس: 9:00 ص - 5:00 م" : "Sunday - Thursday: 9:00 AM - 5:00 PM"}
                    <br />
                    {isAr ? "الجمعة - السبت: مغلق" : "Friday - Saturday: Closed"}
                  </p>
                </div>
              </div>
            </div>

            <div className="luxe-card p-6" style={{ background: "var(--beige)", borderColor: "var(--gold)" }}>
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "var(--gold-deep)" }} />
                <div>
                  <p className={`${isAr ? "font-arabic" : ""} font-medium text-sm`} style={{ color: "var(--heading)" }}>
                    {isAr ? "استشارة طارئة؟" : "Emergency consultation?"}
                  </p>
                  <p className={`mt-1 ${isAr ? "font-arabic" : ""} text-xs leading-relaxed`} style={{ color: "var(--ink-soft)" }}>
                    {isAr ? "اتصل مباشرة على الواتساب وسنرد خلال 30 دقيقة." : "Message us on WhatsApp and we'll reply within 30 minutes."}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* FORM */}
          <div className="lg:col-span-7">
            <div className="luxe-card p-8" style={{ background: "var(--paper)" }}>
              <p className="eyebrow">{isAr ? "نموذج تواصل" : "Contact form"}</p>
              <h2 className={`mt-3 ${isAr ? "font-arabic" : "font-display"} text-2xl lg:text-3xl`}
                  style={{ fontWeight: isAr ? 800 : 500, color: "var(--heading)" }}>
                {isAr ? "أرسل لنا استفسارك" : "Send us your enquiry"}
              </h2>
              <form onSubmit={onSubmit} className="mt-8 space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="c-name" className={isAr ? "font-arabic" : ""}>{isAr ? "الاسم الكامل" : "Full name"}</Label>
                    <Input id="c-name" data-testid="contact-name" value={form.name}
                           onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
                           className="mt-2 h-12 rounded-sm" required />
                  </div>
                  <div>
                    <Label htmlFor="c-email" className={isAr ? "font-arabic" : ""}>{isAr ? "البريد" : "Email"}</Label>
                    <Input id="c-email" type="email" data-testid="contact-email" value={form.email}
                           onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
                           className="mt-2 h-12 rounded-sm" dir="ltr" required />
                  </div>
                  <div>
                    <Label htmlFor="c-phone" className={isAr ? "font-arabic" : ""}>{isAr ? "الهاتف" : "Phone"}</Label>
                    <Input id="c-phone" data-testid="contact-phone" value={form.phone}
                           onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))}
                           className="mt-2 h-12 rounded-sm" dir="ltr" />
                  </div>
                  <div>
                    <Label htmlFor="c-subject" className={isAr ? "font-arabic" : ""}>{isAr ? "الموضوع" : "Subject"}</Label>
                    <Input id="c-subject" data-testid="contact-subject" value={form.subject}
                           onChange={(e) => setForm((s) => ({ ...s, subject: e.target.value }))}
                           className="mt-2 h-12 rounded-sm" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="c-message" className={isAr ? "font-arabic" : ""}>{isAr ? "كيف يمكننا المساعدة؟" : "How can we help?"}</Label>
                  <Textarea id="c-message" data-testid="contact-message" rows={6} value={form.message}
                            onChange={(e) => setForm((s) => ({ ...s, message: e.target.value }))}
                            className="mt-2 rounded-sm" required />
                </div>
                <Button type="submit" disabled={busy} data-testid="contact-submit"
                        className="btn-gold rounded-sm h-12 px-8">
                  {busy ? (isAr ? "جاري الإرسال..." : "Sending...") : (isAr ? "إرسال الرسالة" : "Send message")}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
